// useMosaicSelection — the Mosaic picker state (tap to place a cell, tap to remove).
//
// Ports web/mosaic.js's picker: tapping a gallery item PLACES it in the next grid
// cell (showing a 1..N badge for where it lands); tapping it again removes it and
// the rest renumber. Tap order = cell order, so the owner controls both *which*
// media and *where*. Capped at MOSAIC_MAX_SELECTION. Pure React state (no cores
// import beyond the cap constant) — the overlay consumes the ordered selection.

import { useCallback, useMemo, useState } from "react";
import type { MediaItem } from "./api";
import { MOSAIC_MAX_SELECTION } from "./cores";

export interface MosaicSelection {
  /** The picked keys, in tap order (= cell order). */
  readonly keys: string[];
  /** Is the picker active? (the gallery shows badges + the Mosaic bar) */
  readonly active: boolean;
  /** Enter the picker. */
  begin(): void;
  /** Leave the picker and clear the selection. */
  cancel(): void;
  /** The 1-based cell number for `key`, or 0 if not selected. */
  positionOf(key: string): number;
  /** Toggle a key in/out of the selection (no-op past the cap when adding). */
  toggle(key: string): void;
  /** Move the cell at index `from` to index `to` — cell reorder before encode. */
  reorder(from: number, to: number): void;
  /** Clear the picks but STAY in pick mode (the "Clear" control). */
  clear(): void;
  /** Seed the newest `cap` items and enter the picker — the zero-config "Mosaic newest N". */
  seedNewest(items: MediaItem[], cap?: number): void;
  /** Resolve the ordered selection to the matching MediaItems (for the overlay). */
  resolve(items: MediaItem[]): MediaItem[];
  /** True at the cap — further adds are ignored. */
  readonly full: boolean;
}

export function useMosaicSelection(): MosaicSelection {
  const [active, setActive] = useState(false);
  const [keys, setKeys] = useState<string[]>([]);

  const begin = useCallback(() => setActive(true), []);
  const cancel = useCallback(() => {
    setActive(false);
    setKeys([]);
  }, []);

  const positionOf = useCallback(
    (key: string) => {
      const i = keys.indexOf(key);
      return i < 0 ? 0 : i + 1;
    },
    [keys],
  );

  const toggle = useCallback((key: string) => {
    setKeys((prev) => {
      const i = prev.indexOf(key);
      if (i >= 0) return prev.filter((k) => k !== key); // remove + renumber
      if (prev.length >= MOSAIC_MAX_SELECTION) return prev; // at the cap
      return [...prev, key];
    });
  }, []);

  const reorder = useCallback((from: number, to: number) => {
    setKeys((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) {
        return prev;
      }
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }, []);

  const clear = useCallback(() => setKeys([]), []);

  const seedNewest = useCallback(
    (items: MediaItem[], cap: number = MOSAIC_MAX_SELECTION) => {
      setKeys(items.slice(0, cap).map((it) => it.key));
      setActive(true);
    },
    [],
  );

  const resolve = useCallback(
    (items: MediaItem[]): MediaItem[] => {
      const byKey = new Map(items.map((it) => [it.key, it] as const));
      return keys
        .map((k) => byKey.get(k))
        .filter((it): it is MediaItem => it !== undefined);
    },
    [keys],
  );

  const full = keys.length >= MOSAIC_MAX_SELECTION;

  return useMemo(
    () => ({
      keys, active, begin, cancel, positionOf, toggle,
      reorder, clear, seedNewest, resolve, full,
    }),
    [keys, active, begin, cancel, positionOf, toggle, reorder, clear, seedNewest, resolve, full],
  );
}
